/**
 * Scrub-preview thumbnails, indexed by a WebVTT file.
 *
 * Ported from `apps/tv/src/features/video/utils/thumbnailsVtt.ts`, which parses
 * the same DCE-authored files. One thing is added: a cue URL may carry a
 * `#xywh=` media fragment, meaning every cue is a rectangle of one sprite sheet
 * rather than its own image. TV renders the whole URL as an `<Image>` and so
 * would draw the entire sheet; on the web the crop is a `background-position`, so
 * it is cheap to honour and the difference is visible when it is wrong.
 */

export interface ThumbnailCrop {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
}

export interface ThumbnailCue {
  readonly startSec: number
  readonly endSec: number
  /** The image URL with any media fragment stripped. */
  readonly url: string
  /** The region of `url` this cue points at, when it is a sprite sheet. */
  readonly crop: ThumbnailCrop | undefined
}

const TIMESTAMP_PATTERN = /^(?:(?<h>\d{2}):)?(?<m>\d{2}):(?<s>\d{2})[.,](?<ms>\d{3})$/
const CROP_PATTERN = /#xywh=(?:pixel:)?(\d+),(\d+),(\d+),(\d+)$/

const toSeconds = (input: string): number | null => {
  const timestampToken = input.trim().split(/\s+/)[0] ?? ""
  const match = TIMESTAMP_PATTERN.exec(timestampToken)

  if (!match?.groups) {
    return null
  }

  const hours = Number(match.groups.h ?? 0)
  const minutes = Number(match.groups.m)
  const seconds = Number(match.groups.s)
  const milliseconds = Number(match.groups.ms)

  if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds) || Number.isNaN(milliseconds)) {
    return null
  }

  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000
}

const splitCrop = (rawUrl: string): { url: string; crop: ThumbnailCrop | undefined } => {
  const match = CROP_PATTERN.exec(rawUrl)

  if (!match) {
    return { url: rawUrl, crop: undefined }
  }

  const [fragment, x, y, width, height] = match

  return {
    url: rawUrl.slice(0, rawUrl.length - fragment.length),
    crop: { x: Number(x), y: Number(y), width: Number(width), height: Number(height) },
  }
}

/**
 * Skips anything it cannot read rather than throwing. A malformed cue in the
 * middle of an otherwise good file should cost that one preview, not every
 * preview.
 */
export const parseThumbnailsVtt = (input: string): readonly ThumbnailCue[] => {
  const lines = input
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  const cues: ThumbnailCue[] = []

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]

    if (!line?.includes("-->") || index + 1 >= lines.length) {
      continue
    }

    const [startText, endText] = line.split("-->").map(value => value.trim())
    const startSec = startText ? toSeconds(startText) : null
    const endSec = endText ? toSeconds(endText) : null

    if (startSec === null || endSec === null || endSec <= startSec) {
      continue
    }

    const rawUrl = lines[index + 1]

    if (!rawUrl || (!rawUrl.startsWith("http://") && !rawUrl.startsWith("https://"))) {
      continue
    }

    const { url, crop } = splitCrop(rawUrl)

    cues.push({ startSec, endSec, url, crop })
  }

  return cues.sort((a, b) => a.startSec - b.startSec)
}

/**
 * The cue covering `targetSec`, by binary search — this runs on every pointer
 * move across the scrub bar, and a long video indexes into the thousands of cues.
 * Past the last cue's end it answers the last cue, so scrubbing to the very end
 * still previews something.
 */
export const findThumbnailCueForSec = (cues: readonly ThumbnailCue[], targetSec: number): ThumbnailCue | undefined => {
  if (cues.length === 0) {
    return undefined
  }

  let low = 0
  let high = cues.length - 1

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const cue = cues[mid]

    if (!cue) {
      break
    }

    if (targetSec < cue.startSec) {
      high = mid - 1
      continue
    }

    if (targetSec >= cue.endSec) {
      low = mid + 1
      continue
    }

    return cue
  }

  const lastCue = cues[cues.length - 1]

  return lastCue && targetSec >= lastCue.endSec ? lastCue : undefined
}

/**
 * The graph returns `thumbnailsUrl` either absolute or as a CDN-relative path,
 * so it is resolved against `CDN_URL` the way the TV player does.
 */
export const resolveThumbnailsUrl = (url: string | undefined, cdnUrl: string): string | undefined => {
  if (!url) {
    return undefined
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url
  }

  return `${cdnUrl.replace(/\/$/, "")}/${url.replace(/^\//, "")}`
}
